import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { adminKey } from "../api/admin";
import { getSession, signOut } from "../api/content";
import type { SessionInfo } from "../api/types";

const ANONYMOUS: SessionInfo = { kind: "ANONYMOUS", email: null, displayName: null };

interface SessionValue {
  /** Who this browser is signed in as (anonymous until known). */
  session: SessionInfo;
  /** False until the first answer arrives — so a page does not act on "anonymous" too early. */
  ready: boolean;
  /** Ask the server again — after signing in or out somewhere else on the page. */
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

/** Used outside a provider; one shared object, so `refresh` keeps a stable identity. */
const DETACHED: SessionValue = { session: ANONYMOUS, ready: true, refresh: async () => {} };

/**
 * Who the public site is being viewed as: anonymous, a member, or the admin.
 *
 * The credential itself is an HttpOnly cookie the page can never read, so this
 * only asks the server "who am I?" — for the sidebar's sign-in box and the
 * account page. What content is shown is decided entirely server-side.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo>(ANONYMOUS);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setSession(await getSession());
    } catch {
      setSession(ANONYMOUS);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <Ctx.Provider value={{ session, ready, refresh }}>{children}</Ctx.Provider>;
}

/** The current session; anonymous (and inert) outside a SessionProvider, e.g. in tests. */
export function useSession(): SessionValue {
  return useContext(Ctx) ?? DETACHED;
}

/** A friendly name for the signed-in member. */
export function sessionName(session: SessionInfo): string {
  if (session.kind === "ADMIN") return "the site admin";
  return session.displayName || session.email || "member";
}

/**
 * Sign out of everything in this browser — the member or admin cookie session,
 * and the admin console's key if it is held in this tab — then reload from the
 * home page so nothing private stays on screen.
 */
export async function signOutEverywhere(): Promise<void> {
  adminKey.clear();
  try {
    await signOut();
  } finally {
    window.location.assign("/");
  }
}

/**
 * Only same-site paths are allowed as a post-sign-in destination, so a crafted
 * `?next=https://evil.example` link cannot bounce a member off the site.
 */
export function safeNext(next: string | null | undefined): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return "/";
  return next;
}
